import { StreamContextType } from '../../../contexts/eventanalytics/stream';
import axios from 'axios';
import {
  baseURL,
  DESTINATION_TYPES,
  fixedDateRangeValues,
  MAX_FETCH_LIMIT_FOR_CSV_EXPORT,
  pages,
} from '../event-analytics-constants';
import { API_RESPONSE_TYPE, BracketReference, CsvDataObject, STREAM_DATA_PAGES } from '../types';
import { DateTime } from 'luxon';
import { EventAnalyticsState } from '../../../contexts/eventanalytics/event-analytics';
import { dateTimeFormatUTC } from '../date-range-options';

export const fetchSummaryStats = async (
  EventAnalyticsState: EventAnalyticsState
): Promise<BracketReference<number | null>[]> => {
  const {
    customerId,
    token,
    eventId,
    dateRange: { startDate, endDate },
    dateRangeOption,
    viewAllData,
  } = EventAnalyticsState;
  const summaryStatsAPI = `${baseURL}/${customerId}/webevents/statistics/summary`;
  const dateRangeValue = dateRangeOption.value.toString();
  const isFullMonth = fixedDateRangeValues.includes(dateRangeValue);
  try {
    const { data } = await axios.get(summaryStatsAPI, {
      headers: token,
      params: {
        startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
        endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
        viewAllData,
        destinationType: DESTINATION_TYPES.STREAM,
        isFullMonth,
        ...(eventId && { eventId }),
      },
    });
    return data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const fetchMapData = async (EventAnalyticsState: EventAnalyticsState): Promise<API_RESPONSE_TYPE> => {
  const {
    customerId,
    eventId,
    token,
    dateRange: { startDate, endDate },
  } = EventAnalyticsState;
  const url = `${baseURL}/${customerId}/webevents/statistics/viewers/latlong`;
  const defaultParams = {
    startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
    endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
    destinationType: DESTINATION_TYPES.STREAM,
  };
  const params = {
    ...defaultParams,
    ...(eventId && { eventId }),
  };
  try {
    const { data } = await axios.get(url, {
      headers: token,
      params,
    });
    return data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const fetchLineChartData = async (
  EventAnalyticsState: EventAnalyticsState,
  streamAnalyticsState: StreamContextType,
  timeWindowValue: string,
  deviceType: string,
  dataPage: STREAM_DATA_PAGES
): Promise<API_RESPONSE_TYPE> => {
  const {
    customerId,
    eventId,
    token,
    dateRange: { startDate, endDate },
  } = EventAnalyticsState;
  const url = `${baseURL}/${customerId}/webevents/statistics/${timeWindowValue}/${pages[dataPage]}`;
  const defaultParams = {
    startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
    endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
    segmentBy: streamAnalyticsState.groupBy,
    destinationType: DESTINATION_TYPES.STREAM,
  };
  const params = {
    ...defaultParams,
    ...(eventId && { eventId }),
    ...(deviceType && { deviceType }),
    ...(deviceType && { top: 5 }),
  };
  try {
    const { data } = await axios.get(url, {
      headers: token,
      params,
    });
    return data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const fetchData = async (
  EventAnalyticsState: EventAnalyticsState,
  TabValue: string,
  streamAnalyticsState: StreamContextType,
  top?: number
): Promise<API_RESPONSE_TYPE> => {
  const groupBy =
    TabValue === 'deviceType' && streamAnalyticsState.groupBy === 'device_type' ? 'none' : streamAnalyticsState.groupBy;
  const {
    customerId,
    eventId,
    token,
    dateRange: { startDate, endDate },
  } = EventAnalyticsState;
  const url = `${baseURL}/${customerId}/webevents/statistics/viewers/${TabValue}`;
  const defaultParams = {
    segmentBy: groupBy,
    startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
    endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
    destinationType: DESTINATION_TYPES.STREAM,
  };
  const params = { ...defaultParams, ...(eventId && { eventId }), ...(top && { top }) };
  try {
    const { data } = await axios.get(url, {
      headers: token,
      params,
    });
    return data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const exportCSVData = async (
  EventAnalyticsState: EventAnalyticsState,
  offset?: string | number | undefined
): Promise<CsvDataObject[]> => {
  const {
    customerId,
    eventId,
    token,
    dateRange: { startDate, endDate },
  } = EventAnalyticsState;
  const url = `${baseURL}/${customerId}/webevents/export`;
  const defaultParams = {
    startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
    endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
    destinationType: DESTINATION_TYPES.STREAM,
    max: MAX_FETCH_LIMIT_FOR_CSV_EXPORT,
  };
  const params = {
    ...defaultParams,
    ...(eventId && { eventId }),
    ...(offset && { offset }),
  };
  try {
    const { data } = await axios.get(url, {
      headers: token,
      params,
    });
    return data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const fetchMinByMinData = async (
  EventAnalyticsState: EventAnalyticsState,
  streamAnalyticsState: StreamContextType,
  graphInterval: number,
): Promise<API_RESPONSE_TYPE> => {
  const {
    customerId,
    eventId,
    token,
    dateRange: { startDate, endDate },
  } = EventAnalyticsState;
  const url = `${baseURL}/${customerId}/webevents/statistics/events/${eventId}/minByMin`;
  const defaultParams = {
    startDate: DateTime.fromISO(startDate).startOf('day').toFormat(dateTimeFormatUTC),
    endDate: DateTime.fromISO(endDate).endOf('day').toFormat(dateTimeFormatUTC),
    segmentBy: streamAnalyticsState.groupBy,
    destinationType: DESTINATION_TYPES.STREAM,
    graphInterval,
  };
  const params = { ...defaultParams};
  try {
    const { data } = await axios.get(url, {
      headers: token,
      params,
    });
    return data;
  } catch (error) {
    console.error(error);
    return {};
  }
};